import React, { memo, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getLocalDateString } from "@/lib/date";

const DAY_LABELS = ["D", "L", "M", "M", "J", "V", "S"];

function toISO(year, month1, day) {
  return `${year}-${String(month1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

// Always returns exactly 42 cells (6 rows × 7 cols), including overflow days
function buildMonthGrid(year, month) {
  const firstDay  = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = firstDay.getDay(); // 0=Sun

  const cells = [];

  // Leading overflow from prev month
  const prevMonthDays = new Date(year, month, 0).getDate();
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear  = month === 0 ? year - 1 : year;
  for (let i = startOffset - 1; i >= 0; i--) {
    const day = prevMonthDays - i;
    cells.push({ day, iso: toISO(prevYear, prevMonth + 1, day), overflow: true });
  }

  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, iso: toISO(year, month + 1, d), overflow: false });
  }

  // Trailing overflow from next month
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear  = month === 11 ? year + 1 : year;
  let trailing = 1;
  while (cells.length < 42) {
    cells.push({ day: trailing, iso: toISO(nextYear, nextMonth + 1, trailing), overflow: true });
    trailing++;
  }

  return cells;
}

function getMonthLabel(year, month) {
  const s = new Date(year, month, 1).toLocaleDateString("es-MX", { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * activeDates: Map<"YYYY-MM-DD", string[]>  (date → array of Tailwind dotColor classes)
 */
const AgendaMonthView = memo(function AgendaMonthView({
  activeDates = new Map(),
  selected,
  onSelect,
  onDayPress,   // called with iso when user taps a day that has events
}) {
  const today = getLocalDateString();
  const [todayYear, todayMonthIdx] = today.split("-").map(Number);
  const [viewYear, setViewYear] = useState(todayYear);
  const [viewMonth, setViewMonth] = useState(todayMonthIdx - 1); // 0-indexed

  const touchStartX = useRef(null);
  const cells = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);

  const goToPrev = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };
  const goToNext = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };
  const goToToday = () => {
    setViewYear(todayYear);
    setViewMonth(todayMonthIdx - 1);
    onSelect?.(today);
  };

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 44) diff > 0 ? goToNext() : goToPrev();
    touchStartX.current = null;
  };

  const handleDayTap = (cell) => {
    if (cell.overflow) {
      // Navigate to that month on tap of overflow day
      const [y, m] = cell.iso.split("-").map(Number);
      setViewYear(y);
      setViewMonth(m - 1);
      return;
    }
    onSelect?.(selected === cell.iso ? null : cell.iso);
    const dots = activeDates.get(cell.iso);
    if (dots?.length && onDayPress) onDayPress(cell.iso);
  };

  const isCurrentMonth = viewYear === todayYear && viewMonth === todayMonthIdx - 1;

  return (
    <div
      className="bg-white dark:bg-slate-800 mx-4 rounded-[20px] border border-slate-200/50 dark:border-slate-700/60 shadow-sm overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* ── Month navigation header ─────────── */}
      <div className="flex items-center justify-between px-3 py-3 border-b border-slate-50 dark:border-slate-700/60">
        <button
          onClick={goToPrev}
          className="h-11 w-11 rounded-full flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors active:scale-[0.93]"
        >
          <ChevronLeft size={16} />
        </button>

        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold text-slate-800 dark:text-slate-100">
            {getMonthLabel(viewYear, viewMonth)}
          </span>
          {!isCurrentMonth && (
            <button
              onClick={goToToday}
              className="text-[10px] font-semibold text-brand bg-brand/8 hover:bg-brand/15 px-2 py-0.5 rounded-full transition-colors"
            >
              Hoy
            </button>
          )}
        </div>

        <button
          onClick={goToNext}
          className="h-11 w-11 rounded-full flex items-center justify-center text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors active:scale-[0.93]"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* ── Day-of-week labels ───────────────── */}
      <div className="grid grid-cols-7 px-2 pt-2 pb-1">
        {DAY_LABELS.map((d, i) => (
          <div key={i} className="text-center text-[9px] font-bold text-slate-400 dark:text-slate-500">
            {d}
          </div>
        ))}
      </div>

      {/* ── Day cells (always 42 = 6 rows) ───── */}
      <div className="grid grid-cols-7 px-1 pb-2">
        {cells.map((cell, idx) => {
          const isToday    = cell.iso === today && !cell.overflow;
          const isSelected = cell.iso === selected;
          const dots       = activeDates.get(cell.iso) || [];
          const hasDots    = dots.length > 0;
          const extraDots  = dots.length > 3 ? dots.length - 2 : 0;
          const visibleDots = extraDots ? dots.slice(0, 2) : dots.slice(0, 3);

          return (
            <button
              key={`${cell.iso}-${idx}`}
              onClick={() => handleDayTap(cell)}
              className={`
                flex flex-col items-center justify-center py-1.5 rounded-full
                transition-all duration-150 ease-out active:scale-[0.9]
                ${cell.overflow ? "opacity-25" : ""}
                ${isSelected && !cell.overflow
                  ? "bg-brand shadow-[0_2px_8px_rgba(27,79,156,0.3)]"
                  : isToday
                  ? "bg-brand/10"
                  : "hover:bg-slate-50 dark:hover:bg-slate-700/40"}
              `}
            >
              <span className={`
                text-[13px] leading-none font-medium
                ${isSelected && !cell.overflow ? "text-white font-bold"
                  : isToday             ? "text-brand font-bold"
                  : cell.overflow       ? "text-slate-400 dark:text-slate-600"
                  :                       "text-slate-700 dark:text-slate-200"}
              `}>
                {cell.day}
              </span>

              {/* Dots area — fixed height so all cells align */}
              <div className="flex items-center gap-0.5 mt-0.5 h-[5px]">
                {hasDots && visibleDots.map((dotColor, di) => (
                  <span
                    key={di}
                    className={`h-1 w-1 rounded-full ${isSelected ? "bg-white/70" : dotColor}`}
                  />
                ))}
                {extraDots > 0 && (
                  <span className={`text-[8px] leading-none font-bold ${isSelected ? "text-white/70" : "text-slate-400 dark:text-slate-500"}`}>
                    +{extraDots}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
});

export default AgendaMonthView;
